import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/site/PageHero";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";
import { Button } from "@/components/ui/button";
import { Clock, MapPin, Star, Check } from "lucide-react";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setE(null);
    api.get(`/experiences/${slug}`).then((r) => setE(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: e ? `${e.title} in Maharashtra | Maha Sampada` : "Experience | Maha Sampada",
    description: e ? `${e.title}: ${e.intro}` : "",
  });

  if (notFound) return <NotFoundBlock title="Experience Not Found" message="This experience isn't available. Browse all our Maharashtra experiences instead." backTo="/experiences" backLabel="View All Experiences" />;
  if (!e) return <div className="flex min-h-[60vh] items-center justify-center pt-20 font-accent">Loading…</div>;

  return (
    <div>
      <PageHero overline="Experience" title={e.title} subtitle={e.intro} image={e.image}
        crumbs={[{ label: "Experiences", to: "/experiences" }, { label: e.title }]} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-gold/25 bg-white p-6">
                <div className="flex items-center gap-2 font-display text-lg font-semibold text-charcoal"><MapPin className="h-5 w-5 text-saffron" />Suitable Destinations</div>
                <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                  {e.destinations?.map((d) => <li key={d} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-forest" />{d}</li>)}
                </ul>
              </div>
              <div className="rounded-2xl border border-gold/25 bg-white p-6">
                <div className="flex items-center gap-2 font-display text-lg font-semibold text-charcoal"><Clock className="h-5 w-5 text-saffron" />Suggested Duration</div>
                <p className="mt-3 text-sm text-muted-foreground">{e.duration}</p>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-charcoal"><Star className="h-5 w-5 text-saffron" />Major Highlights</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {e.highlights?.map((h) => (
                  <div key={h} className="flex items-center gap-2 rounded-xl border border-gold/25 bg-white px-4 py-3 text-sm text-charcoal"><Check className="h-4 w-4 text-forest" />{h}</div>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-display text-2xl font-semibold text-charcoal">Related Itineraries</h2>
              <Link to="/itineraries" className="mt-3 inline-block font-accent text-sm font-semibold text-saffron hover:underline">Browse our sample itinerary library →</Link>
            </div>
          </div>

          <aside>
            <div className="sticky top-24 rounded-2xl bg-forest p-7 text-cream">
              <h3 className="font-display text-xl font-semibold">Plan This Experience</h3>
              <p className="mt-2 text-sm text-cream/85">Share your client profile and we'll build a customized programme with confidential B2B pricing.</p>
              <Link to={`/enquiry?ref=${encodeURIComponent(e.title)}`} className="mt-5 block">
                <Button data-testid="experience-enquiry-cta" className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">Request B2B Proposal</Button>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
